import { useEffect, useState, type FormEvent } from 'react';
import styled from 'styled-components';
import { Card } from '@shared/ui/Card';
import { Button } from '@shared/ui/Button';
import { Modal } from '@shared/ui/Modal';
import { Field, Input, Label, Select } from '@shared/ui/Input';
import { Badge } from '@shared/ui/Badge';
import {
  useAssignPolyusCodeMutation,
  useCancelPolyusCodeMutation,
  useGetPolyusRulesQuery,
  useListPolyusCodesQuery,
  useRegisterExternalPolyusCodeMutation,
  useResolvePolyusCodeClashMutation,
  type PolyusCodeClash,
  type PolyusCodeEntry,
} from '@shared/api';
import { useAgentPermission } from '@entities/user';
import { pluralizeRu } from '@shared/lib/formatFile';
import { PolyusSendModal } from './PolyusSendModal';

/**
 * Шифры проектной корреспонденции ООО «Полюс Сухой Лог» (письмо ПСЛ-1755 от
 * 16.09.2026, действует с 17.09.2026; Методические указания ОП-04 МУ-088 от
 * 13.10.2026): выдача следующего шифра письму САТКОМ Заказчику, отправка письма
 * Заказчику прямо из ОСНОВЫ (только с официального адреса САТКОМ) и журнал.
 * Номер сквозной в рамках кода САТКОМ (СТКМ) — его ведёт ОСНОВА, без дублей;
 * аннулированный номер повторно не выдаётся. Шифры писем, ушедших мимо ОСНОВЫ,
 * попадают в журнал из «Отправленных» ящика (в том числе с бланка во вложении),
 * из писем Заказчика или вносятся вручную; номер, взятый вручную второй раз,
 * показывается как повтор. Ответы Заказчика на наши письма видны у шифра в
 * журнале.
 */

const Intro = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(4)};
  max-width: 820px;
  font-size: 13px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing(4)};
  margin-bottom: ${({ theme }) => theme.spacing(5)};

  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Title = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing(3)};
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Wide = styled(Field)`
  grid-column: 1 / -1;
`;

const Hint = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Preview = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-top: ${({ theme }) => theme.spacing(4)};
  flex-wrap: wrap;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Mono = styled.code`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-wrap: anywhere;
`;

const BigCode = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-wrap: anywhere;
`;

const Rules = styled.ul`
  margin: ${({ theme }) => `${theme.spacing(2)} 0 0`};
  padding-left: ${({ theme }) => theme.spacing(5)};
  font-size: 13px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};

  li + li {
    margin-top: ${({ theme }) => theme.spacing(1)};
  }
`;

const ErrorText = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.status.danger};
`;

const JournalHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const Search = styled(Input)`
  width: min(340px, 100%);
`;

const TableScroll = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th {
    text-align: left;
    padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.text.muted};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    white-space: nowrap;
  }
  td {
    padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Muted = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Nowrap = styled.td`
  white-space: nowrap;
`;

const CancelledRow = styled.tr`
  td {
    opacity: 0.55;
  }
`;

const ErrorHint = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.status.danger};
  cursor: help;
`;

const HeadActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;

  button {
    flex-shrink: 0;
  }
`;

const ClashBox = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};
  border: 1px solid ${({ theme }) => theme.colors.status.danger};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  line-height: 1.5;
`;

const ClashTitle = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.status.danger};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const ClashRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => `${theme.spacing(2)} 0`};
  border-top: 1px solid ${({ theme }) => theme.colors.border};

  button {
    flex-shrink: 0;
  }
`;

const Buttons = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-top: ${({ theme }) => theme.spacing(2)};

  button {
    white-space: nowrap;
  }
`;

function copy(text: string) {
  void navigator.clipboard?.writeText(text);
}

function errorMessage(e: unknown): string {
  const data = (e as { data?: { error?: { message?: string }; message?: string } })?.data;
  return data?.error?.message ?? data?.message ?? 'Не удалось выдать шифр — попробуйте ещё раз';
}

function dateRu(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU');
}

function dateTimeRu(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Повторы номера: в письме нашёлся шифр с номером, который журнал уже записал
 * за другим шифром. Номер берут вручную, мимо ОСНОВЫ, — два письма Заказчику
 * ушли с одним номером. Разобрали (например, написали Заказчику) — «Разобрано».
 */
function ClashList({
  clashes,
  canWrite,
  onOpenLetter,
}: {
  clashes: PolyusCodeClash[];
  canWrite: boolean;
  onOpenLetter?: (correspondenceId: string) => void;
}) {
  const [resolve, resolveState] = useResolvePolyusCodeClashMutation();
  if (clashes.length === 0) return null;
  return (
    <ClashBox role="alert">
      <ClashTitle>
        Номер повторён:{' '}
        {clashes.length === 1
          ? 'одно письмо'
          : `${clashes.length} ${pluralizeRu(clashes.length, ['письмо', 'письма', 'писем'])}`}{' '}
        с номером, который уже занят
      </ClashTitle>
      <Muted>
        За уникальность шифров по МУ ОП-04 МУ-088 отвечает САТКОМ. Берите номер только кнопкой
        «Присвоить шифр» — тогда повторов не будет.
      </Muted>
      {clashes.map((x) => (
        <ClashRow key={x.id}>
          <div>
            <Mono>{x.code}</Mono> — номер {String(x.seq).padStart(5, '0')} уже у{' '}
            <Mono>{x.existing_code}</Mono>
            <Hint>
              {x.letter_direction === 'outgoing' ? 'Наше письмо' : 'Письмо'} от{' '}
              {dateTimeRu(x.letter_at)}
              {x.letter_subject ? ` · ${x.letter_subject}` : ''}
            </Hint>
          </div>
          <Buttons>
            {onOpenLetter && (
              <Button
                type="button"
                $size="sm"
                $variant="ghost"
                onClick={() => onOpenLetter(x.correspondence_id)}
              >
                Письмо
              </Button>
            )}
            {canWrite && (
              <Button
                type="button"
                $size="sm"
                $variant="secondary"
                disabled={resolveState.isLoading}
                onClick={() => void resolve(x.id)}
              >
                Разобрано
              </Button>
            )}
          </Buttons>
        </ClashRow>
      ))}
    </ClashBox>
  );
}

/** Статус письма в журнале. */
function StatusBadge({ e }: { e: PolyusCodeEntry }) {
  if (e.status === 'cancelled') {
    return (
      <Badge $tone="warning" title={e.cancel_reason ?? undefined}>
        Аннулирован
      </Badge>
    );
  }
  if (e.status === 'sending') return <Badge $tone="info">Отправляется…</Badge>;
  if (e.status === 'sent') {
    const how =
      e.source === 'mailbox'
        ? 'найдено в «Отправленных»'
        : e.source === 'manual'
          ? 'внесено вручную'
          : `из ОСНОВЫ: ${e.sent_from ?? ''} → ${e.sent_to ?? ''}`;
    return (
      <>
        <Badge $tone="success" title={how}>
          Отправлено
        </Badge>
        {e.sent_at && <Hint>{dateTimeRu(e.sent_at)}</Hint>}
        {e.source !== 'osnova' && <Hint>{how}</Hint>}
      </>
    );
  }
  return (
    <>
      <Badge $tone="neutral">Не отправлено</Badge>
      {e.send_error && <ErrorHint title={e.send_error}>Ошибка отправки</ErrorHint>}
    </>
  );
}

export function PolyusCodesTab({
  onOpenLetter,
}: {
  /** Открыть письмо в карточке Диспетчера (id в реестре корреспонденции). */
  onOpenLetter?: (correspondenceId: string) => void;
}) {
  const { can_write } = useAgentPermission('dispatcher');
  const { data: rules } = useGetPolyusRulesQuery();
  const [project, setProject] = useState('');
  const [discipline, setDiscipline] = useState('');
  const [subject, setSubject] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [outgoingNumber, setOutgoingNumber] = useState('');
  const [issued, setIssued] = useState<PolyusCodeEntry | null>(null);
  const [assign, assignState] = useAssignPolyusCodeMutation();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const { data: journal, isLoading } = useListPolyusCodesQuery({
    search: search || undefined,
    limit: 200,
  });
  const [cancelTarget, setCancelTarget] = useState<PolyusCodeEntry | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelCode, cancelState] = useCancelPolyusCodeMutation();
  const [sendTarget, setSendTarget] = useState<PolyusCodeEntry | null>(null);
  const [externalOpen, setExternalOpen] = useState(false);
  const [extCode, setExtCode] = useState('');
  const [extSubject, setExtSubject] = useState('');
  const [extReplyTo, setExtReplyTo] = useState('');
  const [extOutgoing, setExtOutgoing] = useState('');
  const [extResult, setExtResult] = useState<PolyusCodeEntry | null>(null);
  const [registerExternal, externalState] = useRegisterExternalPolyusCodeMutation();

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput.trim()), 280);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  // Выданный шифр — в актуальном состоянии из журнала (после отправки
  // кнопка «Отправить письмо» пропадает).
  const issuedNow = issued
    ? (journal?.items.find((i) => i.id === issued.id) ?? issued)
    : null;
  const sender = rules?.sender_code ?? 'СТКМ';
  // Отправка из ОСНОВЫ — только с официального адреса САТКОМ; иначе письмо
  // отправляют из почты, а ОСНОВА выдаёт номер и находит письмо в «Отправленных».
  const canSend = Boolean(rules?.sending.enabled);
  const officialFrom = rules?.sending.official_from ?? 'sec@satkom.su';
  const next = String(rules?.next_seq ?? 1).padStart(5, '0');
  const preview = `LET-${project || '<проект>'}-${sender}-SL-${discipline || '<дисциплина>'}-${next}`;
  const ready = Boolean(project && discipline && subject.trim());

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!ready || !can_write) return;
    try {
      const entry = await assign({
        project,
        discipline,
        subject: subject.trim(),
        reply_to: replyTo.trim() || undefined,
        outgoing_number: outgoingNumber.trim() || undefined,
      }).unwrap();
      setIssued(entry);
      setSubject('');
      setReplyTo('');
      setOutgoingNumber('');
    } catch {
      // Сообщение — в assignState.error.
    }
  }

  async function onRegisterExternal() {
    try {
      const entry = await registerExternal({
        code: extCode.trim(),
        subject: extSubject.trim(),
        reply_to: extReplyTo.trim() || undefined,
        outgoing_number: extOutgoing.trim() || undefined,
      }).unwrap();
      setExtResult(entry);
      setExtCode('');
      setExtSubject('');
      setExtReplyTo('');
      setExtOutgoing('');
    } catch {
      // Сообщение — в externalState.error.
    }
  }

  async function onCancel() {
    if (!cancelTarget) return;
    try {
      await cancelCode({ id: cancelTarget.id, reason: cancelReason.trim() || undefined }).unwrap();
      setCancelTarget(null);
      setCancelReason('');
    } catch {
      // Сообщение — в cancelState.error.
    }
  }

  return (
    <>
      <Intro>
        С 17.09.2026 ООО «Полюс Сухой Лог» принимает проектную корреспонденцию только с шифром
        (письмо ПСЛ-1755, Методические указания ОП-04 МУ-088; коды дисциплин — по Приложению 1 к
        ним). Шифр письма САТКОМ: <Mono>LET-проект-{sender}-SL-дисциплина-номер</Mono>. Номер
        сквозной для всех писем САТКОМ Заказчику — его выдаёт ОСНОВА, без повторов и пропусков.{' '}
        {canSend ? (
          <>
            Присвойте шифр, поставьте его на бланк и отправьте письмо кнопкой «Отправить» — оно уйдёт
            на {rules?.sending.to ?? 'slgold@polyus.com'}
            {rules?.sending.from_email ? ` с ${rules.sending.from_email}` : ''}.
          </>
        ) : (
          <>
            Присвойте шифр, поставьте его на бланк и отправьте письмо из почты{' '}
            <Mono>{officialFrom}</Mono> на {rules?.sending.to ?? 'slgold@polyus.com'} — диспетчер
            найдёт его в «Отправленных» и отметит шифр отправленным.
          </>
        )}{' '}
        Шифры во входящих и отправленных письмах — в теме, тексте и на бланке во вложении —
        диспетчер находит сам и вносит в журнал, а ответы Заказчика («ПСЛ-2100 от …» со ссылкой на
        наш шифр или исх. №) показывает у шифра в журнале.
      </Intro>

      <Grid>
        <Card>
          <Title>Присвоить шифр письму</Title>
          <form onSubmit={onSubmit}>
            <FormGrid>
              <Field>
                <Label htmlFor="pc-project">Проект *</Label>
                <Select
                  id="pc-project"
                  value={project}
                  onChange={(e) => setProject(e.target.value)}
                  disabled={!can_write}
                >
                  <option value="">Выберите проект</option>
                  {rules?.projects.map((p) => (
                    <option key={p.code} value={p.code}>
                      {p.code} — {p.name}
                    </option>
                  ))}
                </Select>
                <Hint>Письмо по нескольким проектам — ПСЛ.</Hint>
              </Field>
              <Field>
                <Label htmlFor="pc-discipline">Дисциплина *</Label>
                <Select
                  id="pc-discipline"
                  value={discipline}
                  onChange={(e) => setDiscipline(e.target.value)}
                  disabled={!can_write}
                >
                  <option value="">Выберите дисциплину</option>
                  {rules?.disciplines.map((d) => (
                    <option key={d.code} value={d.code}>
                      {d.code} — {d.name}
                    </option>
                  ))}
                </Select>
                <Hint>По нескольким дисциплинам — GEN (общие вопросы).</Hint>
              </Field>
              <Wide>
                <Label htmlFor="pc-subject">Тема письма *</Label>
                <Input
                  id="pc-subject"
                  value={subject}
                  maxLength={500}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="О допуске сотрудников на объект"
                  disabled={!can_write}
                />
              </Wide>
              <Field>
                <Label htmlFor="pc-reply">На № (письмо Заказчика)</Label>
                <Input
                  id="pc-reply"
                  value={replyTo}
                  maxLength={100}
                  onChange={(e) => setReplyTo(e.target.value)}
                  placeholder="Рег. номер, если это ответ"
                  disabled={!can_write}
                />
              </Field>
              <Field>
                <Label htmlFor="pc-out">Исх. № САТКОМ</Label>
                <Input
                  id="pc-out"
                  value={outgoingNumber}
                  maxLength={100}
                  onChange={(e) => setOutgoingNumber(e.target.value)}
                  placeholder="Номер по журналу исходящих"
                  disabled={!can_write}
                />
              </Field>
            </FormGrid>
            <Preview>
              <span>
                Следующий шифр: <Mono>{preview}</Mono>
              </span>
              <Button
                type="submit"
                disabled={!ready || !can_write || assignState.isLoading}
                title={can_write ? undefined : 'Нужно право записи на агента «Диспетчер»'}
              >
                {assignState.isLoading ? 'Выдаём…' : 'Присвоить шифр'}
              </Button>
            </Preview>
            {assignState.isError && <ErrorText>{errorMessage(assignState.error)}</ErrorText>}
          </form>
        </Card>

        <Card>
          {issued ? (
            <>
              <Title>Шифр выдан</Title>
              <BigCode>
                {issued.code}
                <Button
                  type="button"
                  $size="sm"
                  $variant="secondary"
                  onClick={() => copy(issued.code)}
                >
                  Скопировать
                </Button>
                {issuedNow?.status === 'active' && can_write && canSend && (
                  <Button type="button" $size="sm" onClick={() => setSendTarget(issuedNow)}>
                    Отправить письмо
                  </Button>
                )}
                {issuedNow && issuedNow.status !== 'active' && <StatusBadge e={issuedNow} />}
              </BigCode>
              <Hint>
                {issued.subject} · {issued.project} — {issued.project_name} · {issued.discipline} —{' '}
                {issued.discipline_name}
              </Hint>
              <Rules>
                {(issued.guidance ?? []).map((g) => (
                  <li key={g}>{g}</li>
                ))}
              </Rules>
            </>
          ) : (
            <>
              <Title>Как оформить письмо Заказчику</Title>
              <Rules>
                <li>Шифр — на бланке письма в левом углу под темой.</li>
                {canSend ? (
                  <li>
                    Отправляйте кнопкой «Отправить» в журнале: ОСНОВА назовёт файл письма шифром
                    («LET-…-00003.pdf») и отправит на{' '}
                    <Mono>{rules?.official_email ?? 'slgold@polyus.com'}</Mono>.
                  </li>
                ) : (
                  <li>
                    Отправляйте только с официального адреса САТКОМ <Mono>{officialFrom}</Mono> на{' '}
                    <Mono>{rules?.official_email ?? 'slgold@polyus.com'}</Mono>; файл письма
                    назовите шифром («LET-…-00003.pdf»).
                  </li>
                )}
                <li>Файлы приложений — как они названы в тексте письма («Приложение 1 - …»).</li>
                {canSend ? (
                  <li>
                    Отправляете не из ОСНОВЫ — только на{' '}
                    <Mono>{rules?.official_email ?? 'slgold@polyus.com'}</Mono>, а шифр внесите в
                    журнал кнопкой «Внести шифр».
                  </li>
                ) : (
                  <li>
                    Письмо из почты <Mono>{officialFrom}</Mono> диспетчер найдёт сам; если оно
                    ушло с другого ящика — внесите шифр кнопкой «Внести шифр».
                  </li>
                )}
                <li>
                  Вложения больше {rules?.max_attachment_mb ?? 9} МБ — в VDR, в папку с именем
                  шифра; ссылку отправить на официальный адрес. Доступ к VDR —{' '}
                  <Mono>{rules?.vdr_access_email ?? 'DCC.SLZ@polyus.com'}</Mono>.
                </li>
                <li>В ответе в поле «На №» — регистрационный номер письма Заказчика.</li>
              </Rules>
            </>
          )}
        </Card>
      </Grid>

      <Card>
        <JournalHead>
          <Title>Журнал шифров{journal ? ` · ${journal.total}` : ''}</Title>
          <HeadActions>
            <Search
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Поиск: шифр, тема, «На №», «Исх. №»"
              aria-label="Поиск по журналу шифров"
            />
            {can_write && (
              <Button
                type="button"
                $variant="secondary"
                title="Письмо ушло Заказчику мимо ОСНОВЫ — внесите его шифр, чтобы номера не повторились"
                onClick={() => {
                  externalState.reset();
                  setExtResult(null);
                  setExternalOpen(true);
                }}
              >
                Внести шифр
              </Button>
            )}
          </HeadActions>
        </JournalHead>
        <ClashList
          clashes={journal?.clashes ?? []}
          canWrite={can_write}
          onOpenLetter={onOpenLetter}
        />
        {isLoading ? (
          <Muted>Загрузка…</Muted>
        ) : !journal || journal.items.length === 0 ? (
          <Muted>{search ? 'Ничего не найдено.' : 'Шифров пока нет.'}</Muted>
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <th>Шифр</th>
                  <th>Тема</th>
                  <th>Проект / дисциплина</th>
                  <th>На №</th>
                  <th>Исх. №</th>
                  <th>Выдан</th>
                  <th>Ответ Заказчика</th>
                  <th>Письмо</th>
                </tr>
              </thead>
              <tbody>
                {journal.items.map((e) => {
                  const cells = (
                    <>
                      <Nowrap>
                        <Mono>{e.code}</Mono>
                        {e.problems && e.problems.length > 0 && (
                          <ErrorHint title={e.problems.join('\n')}>Замечание к шифру</ErrorHint>
                        )}
                      </Nowrap>
                      <td>{e.subject}</td>
                      <td>
                        {e.project} · {e.discipline}
                        <Hint>
                          {e.project_name ?? '—'} · {e.discipline_name ?? '—'}
                        </Hint>
                      </td>
                      <td>{e.reply_to ?? <Muted>—</Muted>}</td>
                      <td>{e.outgoing_number ?? <Muted>—</Muted>}</td>
                      <Nowrap>{dateRu(e.created_at)}</Nowrap>
                      <Nowrap>
                        {e.replies && e.replies.length > 0 ? (
                          <Buttons>
                            {e.replies.map((r) => {
                              const label = `${r.customer_reg_no ?? 'Письмо'}${
                                r.customer_reg_date ? ` от ${dateRu(r.customer_reg_date)}` : ''
                              }`;
                              return onOpenLetter ? (
                                <Button
                                  key={r.correspondence_id}
                                  type="button"
                                  $size="sm"
                                  $variant="secondary"
                                  title={r.letter_subject ?? undefined}
                                  onClick={() => onOpenLetter(r.correspondence_id)}
                                >
                                  {label}
                                </Button>
                              ) : (
                                <span key={r.correspondence_id}>{label}</span>
                              );
                            })}
                          </Buttons>
                        ) : (
                          <Muted>—</Muted>
                        )}
                      </Nowrap>
                      <Nowrap>
                        <StatusBadge e={e} />
                        {e.status !== 'cancelled' && (
                          <Buttons>
                            {e.status === 'active' && can_write && canSend && (
                              <Button type="button" $size="sm" onClick={() => setSendTarget(e)}>
                                Отправить
                              </Button>
                            )}
                            {e.correspondence_id && onOpenLetter && (
                              <Button
                                type="button"
                                $size="sm"
                                $variant="secondary"
                                title="Открыть письмо с файлами в карточке Диспетчера"
                                onClick={() => onOpenLetter(e.correspondence_id as string)}
                              >
                                Письмо
                              </Button>
                            )}
                            <Button
                              type="button"
                              $size="sm"
                              $variant="ghost"
                              onClick={() => copy(e.code)}
                            >
                              Скопировать
                            </Button>
                            {e.status === 'active' && can_write && (
                              <Button
                                type="button"
                                $size="sm"
                                $variant="ghost"
                                onClick={() => {
                                  cancelState.reset();
                                  setCancelTarget(e);
                                }}
                              >
                                Аннулировать
                              </Button>
                            )}
                          </Buttons>
                        )}
                      </Nowrap>
                    </>
                  );
                  return e.status === 'cancelled' ? (
                    <CancelledRow key={e.id}>{cells}</CancelledRow>
                  ) : (
                    <tr key={e.id}>{cells}</tr>
                  );
                })}
              </tbody>
            </Table>
          </TableScroll>
        )}
      </Card>

      <Modal
        open={cancelTarget != null}
        title={`Аннулировать шифр ${cancelTarget?.code ?? ''}`}
        onClose={() => !cancelState.isLoading && setCancelTarget(null)}
      >
        <Hint>
          Если письмо с этим шифром так и не ушло Заказчику. Номер останется в журнале и повторно не
          выдаётся.
        </Hint>
        <Field>
          <Label htmlFor="pc-cancel-reason">Причина</Label>
          <Input
            id="pc-cancel-reason"
            value={cancelReason}
            maxLength={500}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Например: письмо не отправлено"
          />
        </Field>
        {cancelState.isError && <ErrorText>{errorMessage(cancelState.error)}</ErrorText>}
        <Preview>
          <span />
          <span>
            <Button
              type="button"
              $variant="secondary"
              onClick={() => setCancelTarget(null)}
              disabled={cancelState.isLoading}
            >
              Отмена
            </Button>{' '}
            <Button
              type="button"
              $variant="danger"
              onClick={onCancel}
              disabled={cancelState.isLoading}
            >
              {cancelState.isLoading ? 'Аннулируем…' : 'Аннулировать'}
            </Button>
          </span>
        </Preview>
      </Modal>

      {sendTarget && (
        <PolyusSendModal
          key={sendTarget.id}
          entry={sendTarget}
          rules={rules}
          onClose={() => setSendTarget(null)}
        />
      )}

      <Modal
        open={externalOpen}
        title="Внести шифр письма, отправленного мимо ОСНОВЫ"
        onClose={() => !externalState.isLoading && setExternalOpen(false)}
      >
        <Hint>
          Если письмо Заказчику ушло не из ОСНОВЫ (например, из VK WorkSpace с sec@satkom.su), внесите
          его шифр: номер займёт место в журнале, и следующие шифры его не повторят. Письма на
          @polyus.com из «Отправленных» ящиков Диспетчера попадают в журнал сами.
        </Hint>
        {extResult ? (
          <>
            <BigCode>{extResult.code}</BigCode>
            <Hint>
              {extResult.created
                ? 'Шифр внесён в журнал.'
                : 'Этот шифр уже был в журнале — отмечен как отправленный.'}{' '}
              Следующий шифр будет{' '}
              {String((rules?.next_seq ?? extResult.seq + 1)).padStart(5, '0')}.
            </Hint>
            <Preview>
              <span />
              <Button type="button" onClick={() => setExternalOpen(false)}>
                Готово
              </Button>
            </Preview>
          </>
        ) : (
          <>
            <FormGrid>
              <Wide>
                <Label htmlFor="pc-ext-code">Шифр *</Label>
                <Input
                  id="pc-ext-code"
                  value={extCode}
                  maxLength={60}
                  onChange={(e) => setExtCode(e.target.value)}
                  placeholder={`LET-ПСЛ.К-${sender}-SL-GEN-00005`}
                />
              </Wide>
              <Wide>
                <Label htmlFor="pc-ext-subject">Тема письма *</Label>
                <Input
                  id="pc-ext-subject"
                  value={extSubject}
                  maxLength={500}
                  onChange={(e) => setExtSubject(e.target.value)}
                />
              </Wide>
              <Field>
                <Label htmlFor="pc-ext-reply">На № (письмо Заказчика)</Label>
                <Input
                  id="pc-ext-reply"
                  value={extReplyTo}
                  maxLength={100}
                  onChange={(e) => setExtReplyTo(e.target.value)}
                />
              </Field>
              <Field>
                <Label htmlFor="pc-ext-out">Исх. № САТКОМ</Label>
                <Input
                  id="pc-ext-out"
                  value={extOutgoing}
                  maxLength={100}
                  onChange={(e) => setExtOutgoing(e.target.value)}
                />
              </Field>
            </FormGrid>
            {externalState.isError && (
              <ErrorText>{errorMessage(externalState.error)}</ErrorText>
            )}
            <Preview>
              <span />
              <span>
                <Button
                  type="button"
                  $variant="secondary"
                  onClick={() => setExternalOpen(false)}
                  disabled={externalState.isLoading}
                >
                  Отмена
                </Button>{' '}
                <Button
                  type="button"
                  onClick={onRegisterExternal}
                  disabled={!extCode.trim() || !extSubject.trim() || externalState.isLoading}
                >
                  {externalState.isLoading ? 'Вносим…' : 'Внести в журнал'}
                </Button>
              </span>
            </Preview>
          </>
        )}
      </Modal>
    </>
  );
}
